import { forwardRef, useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { bnToEnDigits } from '@dd/shared';
import { cn } from '../cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
type Size = 'sm' | 'md' | 'lg';

const V: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-dark active:bg-brand-darker disabled:bg-neutral-300',
  secondary: 'bg-brand-light text-brand-dark hover:bg-red-200 disabled:bg-neutral-100 disabled:text-neutral-400',
  ghost: 'bg-transparent text-ink-2 hover:bg-neutral-100 disabled:text-neutral-300',
  outline: 'bg-white text-ink border border-line hover:bg-neutral-50 disabled:text-neutral-300',
  danger: 'bg-bad text-white hover:bg-red-800 disabled:bg-neutral-300',
  success: 'bg-ok text-white hover:bg-green-800 disabled:bg-neutral-300',
};
const S: Record<Size, string> = { sm: 'h-9 px-3 text-sm rounded-lg', md: 'h-11 px-4 text-[15px] rounded-xl', lg: 'h-13 px-5 text-base rounded-xl min-h-[52px]' };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'primary', size = 'md', loading, block, icon, className, children, disabled, type = 'button', ...rest }, ref) {
  return (
    <button ref={ref} type={type} disabled={disabled || loading} className={cn('inline-flex select-none items-center justify-center gap-2 font-semibold transition-colors disabled:cursor-not-allowed', V[variant], S[size], block && 'w-full', className)} {...rest}>
      {loading ? <Loader2 className="animate-[dd-spin_0.8s_linear_infinite]" size={18} /> : icon}
      {children}
    </button>
  );
});

export function IconButton({ label, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button type="button" aria-label={label} title={label} className={cn('inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-2 hover:bg-neutral-100 active:bg-neutral-200', className)} {...rest}>
      {children}
    </button>
  );
}

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  className?: string;
}

export function Field({ label, hint, error, required, className, children, htmlFor }: FieldProps & { children: ReactNode; htmlFor?: string }) {
  return (
    <div className={cn('block', className)}>
      {label && (
        <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-ink-2">
          {label}
          {required && <span className="text-bad"> *</span>}
        </label>
      )}
      {children}
      {error ? <p className="mt-1 text-sm text-bad" role="alert">{error}</p> : hint ? <p className="mt-1 text-xs text-ink-3">{hint}</p> : null}
    </div>
  );
}

const inputCls = 'w-full rounded-xl border bg-white px-3 text-[16px] text-ink placeholder:text-neutral-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:bg-neutral-50 disabled:text-neutral-400';

export const Input = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> & FieldProps & { prefix?: ReactNode; suffix?: ReactNode }>(function Input({ label, hint, error, required, className, prefix, suffix, id, ...rest }, ref) {
  const auto = useId();
  const iid = id ?? auto;
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className} htmlFor={iid}>
      <div className="relative">
        {prefix && <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-3">{prefix}</span>}
        <input ref={ref} id={iid} aria-invalid={!!error} className={cn(inputCls, 'h-11', error ? 'border-bad' : 'border-line', prefix ? 'pl-9' : '', suffix ? 'pr-10' : '')} {...rest} />
        {suffix && <span className="absolute inset-y-0 right-3 flex items-center text-ink-3">{suffix}</span>}
      </div>
    </Field>
  );
});

/** Numeric text box: accepts Bangla digits, stores English digits (Bible: input in either script). */
export const NumberInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'prefix'> & FieldProps & { value: string; onChange: (v: string) => void; decimals?: number; prefix?: ReactNode; suffix?: ReactNode }>(function NumberInput({ value, onChange, decimals = 2, ...rest }, ref) {
  return (
    <Input
      ref={ref}
      inputMode={decimals > 0 ? 'decimal' : 'numeric'}
      autoComplete="off"
      value={value}
      onChange={(e) => {
        let v = bnToEnDigits(e.target.value).replace(/[^0-9.]/g, '');
        const dot = v.indexOf('.');
        if (dot >= 0) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '').slice(0, decimals);
        if (decimals === 0) v = v.replace(/\./g, '');
        onChange(v);
      }}
      {...rest}
    />
  );
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & FieldProps>(function TextArea({ label, hint, error, required, className, id, rows = 3, ...rest }, ref) {
  const auto = useId();
  const iid = id ?? auto;
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className} htmlFor={iid}>
      <textarea ref={ref} id={iid} rows={rows} className={cn(inputCls, 'py-2.5', error ? 'border-bad' : 'border-line')} {...rest} />
    </Field>
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & FieldProps>(function Select({ label, hint, error, required, className, id, children, ...rest }, ref) {
  const auto = useId();
  const iid = id ?? auto;
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className} htmlFor={iid}>
      <select ref={ref} id={iid} className={cn(inputCls, 'h-11 appearance-none bg-[url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%2716%27 height=%2716%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%2364748B%27 stroke-width=%272.5%27><path d=%27m6 9 6 6 6-6%27/></svg>")] bg-[length:16px] bg-[right_12px_center] bg-no-repeat pr-9', error ? 'border-bad' : 'border-line')} {...rest}>
        {children}
      </select>
    </Field>
  );
});

export function Switch({ checked, onChange, label, disabled, hint }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean; hint?: ReactNode }) {
  return (
    <label className={cn('flex items-center justify-between gap-3 py-2', disabled && 'opacity-50')}>
      {(label || hint) && (
        <span className="min-w-0">
          <span className="block text-[15px] text-ink">{label}</span>
          {hint && <span className="block text-xs text-ink-3">{hint}</span>}
        </span>
      )}
      <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)} className={cn('relative h-7 w-12 shrink-0 rounded-full transition-colors', checked ? 'bg-brand' : 'bg-neutral-300')}>
        <span className={cn('absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all', checked ? 'left-[22px]' : 'left-0.5')} />
      </button>
    </label>
  );
}

export function Checkbox({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 py-1 text-[15px] text-ink">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4.5 w-4.5 accent-brand" />
      <span>{label}</span>
    </label>
  );
}

export function Segmented<T extends string>({ value, onChange, options, className }: { value: T; onChange: (v: T) => void; options: ReadonlyArray<{ value: T; label: ReactNode; badge?: ReactNode }>; className?: string }) {
  return (
    <div role="tablist" className={cn('no-scrollbar flex gap-1 overflow-x-auto rounded-xl bg-neutral-100 p-1', className)}>
      {options.map((o) => (
        <button key={o.value} role="tab" aria-selected={value === o.value} type="button" onClick={() => onChange(o.value)} className={cn('flex h-9 min-w-fit flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-lg px-3 text-sm font-semibold transition-colors', value === o.value ? 'bg-white text-cat-deep shadow-sm ring-1 ring-cat-mid' : 'text-ink-3')}>
          {o.label}
          {o.badge}
        </button>
      ))}
    </div>
  );
}

export function Chip({ active, onClick, children, className }: { active?: boolean; onClick?: () => void; children: ReactNode; className?: string }) {
  return (
    <button type="button" onClick={onClick} className={cn('inline-flex h-8 shrink-0 items-center gap-1 rounded-full border px-3 text-sm font-medium transition-colors', active ? 'border-brand bg-brand-light text-brand-dark' : 'border-line bg-white text-ink-2', className)}>
      {children}
    </button>
  );
}
